import React from "react"
import { RenderOptions, PropsType } from "./types"

function parse(html: String) {
  let doc
  if (typeof DOMParser === "undefined") {
    return null
  }

  try {
    // eslint-disable-next-line no-undef
    doc = new DOMParser().parseFromString(
      `<!DOCTYPE html>\n<html><body>${html}</body></html>`,
      "text/html",
    )
  } catch (err) {
    return null
  }

  return doc
}

function getProps(attrs: NamedNodeMap) {
  const len = attrs && attrs.length
  if (!len) {
    return undefined
  }

  return Array.from(attrs).reduce((props, attr) => {
    let value: string | boolean = attr.value
    if (value === "") {
      value = true
    }
    props[attr.name] = value
    return props
  }, {} as PropsType)
}

function toVdom(node: Node, { renderNode, renderText }: RenderOptions) {
  function walkChildren(children: NodeListOf<ChildNode>) {
    const c = Array.from(children).map(walk).filter(Boolean)
    if (!c || !c.length) {
      return null
    }
    return React.Children.map(c, (child, i) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, { key: `child-${i}` })
      }
      return child
    })
  }

  function walk(n: Node, index: Number): React.ReactNode {
    if (n.nodeType === 3) {
      let text = n.nodeValue
      if ("textContent" in n) {
        text = n.textContent
      }
      return renderText(text)
    }

    const nodeName = String(n.nodeName).toLowerCase()

    if (n.nodeType !== 1 || ["script", "style"].includes(nodeName)) {
      return undefined
    }

    if (!(n instanceof Element)) {
      return undefined
    }

    const out = renderNode(
      nodeName,
      {
        ...getProps(n.attributes),
        key: `${nodeName}${n.nodeType}-${index}`,
      },
      walkChildren(n.childNodes),
    )

    return out
  }

  return walk(node, 0)
}

export function parseHtmlToComponents(
  html: String,
  { renderNode, renderText }: RenderOptions,
): React.ReactNode {
  const dom = parse(html)
  const body = (dom && dom.body) || dom
  if (!body) {
    return null
  }

  const vdom = toVdom(body, { renderNode, renderText })
  if (vdom) {
    return (vdom as React.ReactElement)?.props?.children
  }

  return null
}
